import React, { useCallback, useRef, useState } from 'react';
import Icon, { IconType } from '../icon';
import * as css from './dropdownButton.module.scss';
import Button from '.';
import classNames from 'classnames';
import useClickedOutsideElement from '../../hooks/useClickedOutsideElement';
import useKeyEventOutsideElement from '../../hooks/useKeyEventOutsideElement';

export interface DropdownButtonOption {
  text: string;
  icon?: IconType;
  onChange?: () => void;
  disabled?: boolean;
  borderTop?: boolean;
}

export const DropdownButton: React.FC<
  React.PropsWithChildren<{
    id: string;
    btnText: string;
    options: DropdownButtonOption[];
    className?: string;
    btnClassname?: string;
    itemClassName?: string;
    disabled?: boolean;
  }>
> = ({
  id,
  options,
  btnText,
  className,
  itemClassName,
  btnClassname,
  disabled,
}) => {
  const [isToggled, setToggled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const [focusIndex, setFocusIndex] = useState(-1);
  const clickedOutside = useClickedOutsideElement<HTMLDivElement>(dropdownRef);
  const keyPressedOutside =
    useKeyEventOutsideElement<HTMLDivElement>(dropdownRef);

  const toggleMenu = () => {
    setFocusIndex(-1);
    setToggled(!isToggled);
  };

  React.useEffect(() => {
    if (clickedOutside || keyPressedOutside) {
      setToggled(false);
    }
  }, [clickedOutside, keyPressedOutside]);

  const iconDirection = isToggled
    ? IconType.CloseRowTriangle
    : IconType.OpenRowTriangle;
  const dropdownId = isToggled ? `dropdownbutton-close` : `dropdownbutton-open`;

  const arrowNavigation = (
    e: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent,
  ) => {
    let tempIndex = focusIndex;

    switch (e.key) {
      case 'ArrowDown':
      case 'Down':
        e.preventDefault();
        while (tempIndex < options.length - 1) {
          if (options[++tempIndex].disabled) {
            continue;
          }
          break;
        }
        (menuRef.current?.childNodes[tempIndex] as HTMLElement)?.focus();
        break;
      case 'ArrowUp':
      case 'Up':
        e.preventDefault();
        while (tempIndex > 0) {
          if (options[--tempIndex].disabled) {
            continue;
          }
          break;
        }
        (menuRef.current?.childNodes[tempIndex] as HTMLElement)?.focus();
        break;
      case 'Tab':
        if (isToggled) toggleMenu();
        break;
    }
    setFocusIndex(tempIndex);
  };

  const [btnWidth, setBtnWidth] = useState<number>(0);

  const btnWrapperRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      const { width } = node.getBoundingClientRect();
      setBtnWidth(width);
    }
  }, []);

  return (
    <div
      className={classNames(css.dropdowncontainer, className || '')}
      ref={dropdownRef}
    >
      <div ref={btnWrapperRef}>
        <Button.Secondary
          onClick={toggleMenu}
          id={id ? `dropdown-${id}` : ''}
          className={classNames(
            isToggled ? css.openButton : css.button,
            btnClassname,
          )}
          aria-expanded={isToggled}
          onKeyDown={arrowNavigation}
          disabled={disabled}
          disableOnClickBlur
        >
          {btnText}
          <Icon
            type={iconDirection}
            htmlId={id ? `${dropdownId}-${id}` : dropdownId}
          />
        </Button.Secondary>
      </div>
      {isToggled && (
        <div
          className={css.dropdownmenu}
          id={id ? `dropdownmenu-${id}` : ''}
          onKeyDown={arrowNavigation}
          style={{
            width: `${btnWidth}px`,
          }}
          ref={menuRef}
        >
          {(options || []).map((option, i) => {
            const clickEvent = (
              e: React.MouseEvent<HTMLDivElement, MouseEvent>,
            ) => {
              if (option.onChange) option.onChange();
              setToggled(false);
            };
            const keyEvent = (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (option.onChange && e.key === 'Enter') option.onChange();
              setToggled(false);
            };

            return (
              <div
                id={`${id}-dropdownbuttonitem-${i}`}
                aria-disabled={!!option.disabled}
                key={i}
                ref={itemRef}
                tabIndex={option.disabled ? undefined : 0}
                className={classNames(
                  css.dropdownitem,
                  option.borderTop && css.borderTop,
                  option.disabled && css.disabled,
                  itemClassName,
                )}
                onClick={clickEvent}
                onKeyPress={keyEvent}
                onKeyDown={arrowNavigation}
              >
                {option.icon && <Icon type={option.icon} />}
                {option.text}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
